import { useEffect, useMemo, useRef, useState } from "react";

import Register from "./Register";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";

import axiosInstance from "../axiosInstance";

import {
  MdPeopleOutline,
  MdPersonAdd,
  MdSearch,
  MdRefresh,
  MdClose,
  MdOutlineAdminPanelSettings,
  MdCheckCircleOutline,
  MdCancel,
} from "react-icons/md";

import { BiShield } from "react-icons/bi";

export default function GetAllUsers() {
  const API = process.env.REACT_APP_API_URL;

  // =========================================================
  // STATE
  // =========================================================

  const [allUsers, setAllUsers] = useState([]);
  const [openModel, setOpenModel] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const toast = useRef(null);

  // =========================================================
  // FETCH USERS
  // =========================================================

  const fecthAllUsers = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await axiosInstance.get(
        `${API}/api/alluser/`
      );

      setAllUsers(res.data || []);
    } catch (err) {
      console.log("Error fetching users:", err);

      setError(
        "Unable to load users. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    fecthAllUsers();
  }, []);

  // =========================================================
  // UPDATE USER STATUS
  // =========================================================

  const toggleUserStatus = async (user) => {
    try {
      await axiosInstance.patch(
        `${API}/api/user/${user.id}/status/`,
        { is_active: !user.is_active }
      );

      await fecthAllUsers();

      toast.current?.show({
        severity: "success",
        summary: "Status Updated",
        detail: `${user.username} is now ${user.is_active ? "inactive" : "active"}.`,
        life: 3000,
      });
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Update Failed",
        detail: err.response?.data?.error || "Unable to update user status.",
        life: 3000,
      });
    }
  };

  // =========================================================
  // SEARCH
  // =========================================================

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) {
      return allUsers;
    }

    const search = searchTerm.toLowerCase().trim();

    return allUsers.filter((user) => {
      return (
        String(user.id || "")
          .toLowerCase()
          .includes(search) ||
        String(user.username || "")
          .toLowerCase()
          .includes(search) ||
        String(user.department || "")
          .toLowerCase()
          .includes(search) ||
        String(user.role || "")
          .toLowerCase()
          .includes(search) ||
        (user.is_active ? "active" : "inactive").includes(
          search
        )
      );
    });
  }, [allUsers, searchTerm]);

  // =========================================================
  // STATISTICS
  // =========================================================

  const totalUsers = allUsers.length;

  const activeUsers = allUsers.filter(
    (user) => user.is_active
  ).length;

  const inactiveUsers = allUsers.filter(
    (user) => !user.is_active
  ).length;

  const adminUsers = allUsers.filter(
    (user) =>
      String(user.role || "").toLowerCase() === "admin"
  ).length;

  // =========================================================
  // STATUS TEMPLATE
  // =========================================================

  const statusBodyTemplate = (rowData) => {
    if (rowData.is_active) {
      return (
        <span
          className="
            inline-flex
            items-center
            gap-2
            px-2.5
            py-1
            rounded-full
            border
            border-emerald-200
            bg-emerald-50
            text-emerald-700
            text-xs
            font-semibold
            whitespace-nowrap
          "
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          Active
        </span>
      );
    }

    return (
      <span
        className="
          inline-flex
          items-center
          gap-2
          px-2.5
          py-1
          rounded-full
          border
          border-red-200
          bg-red-50
          text-red-700
          text-xs
          font-semibold
          whitespace-nowrap
        "
      >
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
        Inactive
      </span>
    );
  };

  // =========================================================
  // ROLE TEMPLATE
  // =========================================================

  const roleBodyTemplate = (rowData) => {
    const role = rowData.role || "—";

    const normalizedRole = String(role).toLowerCase();

    let style =
      "bg-slate-100 text-slate-700 border-slate-200";

    if (normalizedRole === "admin") {
      style =
        "bg-violet-50 text-violet-700 border-violet-200";
    }

    if (normalizedRole === "support") {
      style =
        "bg-sky-50 text-sky-700 border-sky-200";
    }

    if (normalizedRole === "client") {
      style =
        "bg-indigo-50 text-indigo-700 border-indigo-200";
    }

    return (
      <span
        className={`
          inline-flex
          items-center
          gap-1.5
          px-2.5
          py-1
          rounded-md
          border
          text-xs
          font-semibold
          capitalize
          whitespace-nowrap
          ${style}
        `}
      >
        <BiShield size={14} />

        {role}
      </span>
    );
  };

  // =========================================================
  // DEPARTMENT TEMPLATE
  // =========================================================

  const departmentBodyTemplate = (rowData) => {
    return rowData.department ? (
      <span
        className="
          inline-flex
          px-2.5
          py-1
          rounded-md
          bg-slate-100
          border
          border-slate-200
          text-xs
          font-medium
          text-slate-700
          whitespace-nowrap
        "
      >
        {rowData.department}
      </span>
    ) : (
      <span className="text-slate-400">—</span>
    );
  };

// BRANCH
  const branchBodyTemplate = (rowData) => {
    return rowData.branch ? (
      <span
        className="
          inline-flex
          px-2.5
          py-1
          rounded-md
          bg-slate-100
          border
          border-slate-200
          text-xs
          font-medium
          text-slate-700
          whitespace-nowrap
        "
      >
        {rowData.branch}
      </span>
    ) : (
      <span className="text-slate-400">—</span>
    );
  };

  // =========================================================
  // USERNAME TEMPLATE
  // =========================================================

  const usernameBodyTemplate = (rowData) => {
    const username = rowData.username || "Unknown";

    const initials = username
      .trim()
      .split(" ")
      .map((name) => name.charAt(0))
      .join("")
      .substring(0, 2)
      .toUpperCase();

    return (
      <div className="flex items-center gap-3">

        <div
          className="
            w-9
            h-9
            rounded-full
            bg-sky-50
            border
            border-sky-100
            text-sky-700
            flex
            items-center
            justify-center
            text-xs
            font-bold
            flex-shrink-0
          "
        >
          {initials}
        </div>

        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-800 truncate max-w-[180px]">
            {username}
          </p>

          <p className="text-[11px] text-slate-400">
            User ID: {rowData.id}
          </p>
        </div>

      </div>
    );
  };

  // =========================================================
  // ACTION TEMPLATE
  // =========================================================

  const actionBodyTemplate = (rowData) => {
    return (
      <button
        type="button"
        title={rowData.is_active ? "Deactivate user" : "Activate user"}
        onClick={() => toggleUserStatus(rowData)}
        className="
          inline-flex
          items-center
          justify-center
          gap-1.5
          px-2.5
          py-1.5
          rounded-lg
          border
          border-sky-200
          bg-white
          text-sky-600
          text-xs
          font-medium
          hover:bg-sky-50
          hover:border-sky-300
          transition
        "
      >
        {rowData.is_active ? <MdCancel size={16} /> : <MdCheckCircleOutline size={16} />}

        {rowData.is_active ? "Deactivate" : "Activate"}
      </button>
    );
  };

  // =========================================================
  // STAT CARD
  // =========================================================

  const StatCard = ({
    title,
    value,
    subtitle,
    icon,
    iconStyle,
  }) => {
    return (
      <div
        className="
          bg-white
          border
          border-slate-200
          rounded-xl
          p-4
          shadow-sm
          hover:shadow-md
          transition-shadow
        "
      >
        <div className="flex items-start justify-between">

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {title}
            </p>

            <p className="mt-2 text-2xl font-bold text-slate-800">
              {value}
            </p>

            <p className="mt-1 text-xs text-slate-400">
              {subtitle}
            </p>
          </div>

          <div
            className={`
              w-10
              h-10
              rounded-lg
              flex
              items-center
              justify-center
              ${iconStyle}
            `}
          >
            {icon}
          </div>

        </div>
      </div>
    );
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="w-full mt-10 min-h-screen bg-slate-50">

      <div className="w-full max-w-[1800px] mx-auto">

        {/* =================================================
            PAGE HEADER
        ================================================= */}

        <div
          className="
            bg-white
            border
            border-slate-200
            rounded-xl
            shadow-sm
            px-4
            sm:px-5
            lg:px-6
            py-4
            sm:py-5
            mb-5
          "
        >
          <div
            className="
              flex
              flex-col
              sm:flex-row
              sm:items-center
              sm:justify-between
              gap-4
            "
          >

            <div>

              <div className="flex items-center gap-3">

                <div
                  className="
                    w-10
                    h-10
                    rounded-lg
                    bg-sky-50
                    text-sky-600
                    flex
                    items-center
                    justify-center
                  "
                >
                  <MdPeopleOutline size={22} />
                </div>

                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-800">
                    Admin Users
                  </h1>

                  <p className="text-sm text-slate-500 mt-0.5">
                    Manage users, roles and access
                  </p>
                </div>

              </div>

            </div>

            {/* ADD USER */}

            <button
              type="button"
              onClick={() => setOpenModel(true)}
              className="
                inline-flex
                items-center
                justify-center
                gap-2
                w-full
                sm:w-auto
                px-4
                py-2.5
                rounded-lg
                bg-sky-600
                hover:bg-sky-700
                text-white
                text-sm
                font-semibold
                shadow-sm
                transition
              "
            >
              <MdPersonAdd size={19} />

              Add User
            </button>

          </div>
        </div>

        {/* =================================================
            STATISTICS
        ================================================= */}

        <div
          className="
            grid
            grid-cols-1
            sm:grid-cols-2
            xl:grid-cols-4
            gap-3
            sm:gap-4
            mb-5
          "
        >

          <StatCard
            title="Total Users"
            value={totalUsers}
            subtitle="Registered users"
            icon={
              <MdPeopleOutline size={21} />
            }
            iconStyle="bg-sky-50 text-sky-600"
          />

          <StatCard
            title="Active Users"
            value={activeUsers}
            subtitle="Currently active"
            icon={
              <MdCheckCircleOutline size={21} />
            }
            iconStyle="bg-emerald-50 text-emerald-600"
          />

          <StatCard
            title="Inactive Users"
            value={inactiveUsers}
            subtitle="Currently inactive"
            icon={
              <MdCancel size={21} />
            }
            iconStyle="bg-red-50 text-red-600"
          />

          <StatCard
            title="Admin Users"
            value={adminUsers}
            subtitle="Users with admin role"
            icon={
              <MdOutlineAdminPanelSettings size={21} />
            }
            iconStyle="bg-violet-50 text-violet-600"
          />

        </div>

        {/* =================================================
            SEARCH TOOLBAR
        ================================================= */}

        <div
          className="
            
            rounded-xl
           
            p-4
            mb-5
          "
        >

          {/* <div className="flex items-center gap-2 mb-3">

            <MdSearch
              size={18}
              className="text-slate-500"
            />

            <h2 className="text-sm font-semibold text-slate-700">
              User Directory
            </h2>

          </div> */}

          <div className="flex flex-col sm:flex-row gap-3">

            {/* SEARCH */}

            <div className="relative flex-1">

              <MdSearch
                size={19}
                className="
                  absolute
                  left-3
                  top-1/2
                  -translate-y-1/2
                  text-slate-400
                "
              />

              <input
                type="text"
                value={searchTerm}
                onChange={(e) =>
                  setSearchTerm(e.target.value)
                }
                placeholder="Search username, department, role..."
                className="
                bg-white
                  w-1/2
                  h-10
                  pl-10
                  pr-10
                  rounded-lg
                  border
                  border-slate-200
                  
                  text-sm
                  text-slate-700
                  placeholder:text-slate-400
                  outline-none
                  focus:bg-white
                  focus:border-sky-400
                  focus:ring-2
                  focus:ring-sky-100
                  transition
                "
              />

              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="
                    absolute
                    right-2
                    top-1/2
                    -translate-y-1/2
                    w-7
                    h-7
                    flex
                    items-center
                    justify-center
                    rounded-md
                    text-slate-400
                    hover:bg-slate-200
                    hover:text-slate-600
                  "
                >
                  <MdClose size={17} />
                </button>
              )}

            </div>

            {/* REFRESH */}

            <button
              type="button"
              onClick={fecthAllUsers}
              disabled={loading}
              className="
                inline-flex
                items-center
                justify-center
                gap-2
                h-10
                px-4
                rounded-lg
                border
                border-slate-200
                bg-white
                text-slate-600
                text-sm
                font-medium
                hover:bg-slate-50
                disabled:opacity-50
                transition
              "
            >
              <MdRefresh
                size={18}
                className={
                  loading ? "animate-spin" : ""
                }
              />

              Refresh
            </button>

          </div>

          {/* <div className="mt-3 pt-3 border-t border-slate-100">

            <p className="text-xs text-slate-500">

              Showing{" "}

              <span className="font-semibold text-slate-700">
                {filteredUsers.length}
              </span>

              {" "}of{" "}

              <span className="font-semibold text-slate-700">
                {allUsers.length}
              </span>

              {" "}users

            </p>

          </div> */}

        </div>

        {/* =================================================
            REGISTER MODAL
        ================================================= */}

        {openModel && (
          <Register
            close={() => setOpenModel(false)}
            refresh={fecthAllUsers}
          />
        )}

        {/* =================================================
            ERROR STATE
        ================================================= */}

        {error && !loading && (

          <div
            className="
              bg-white
              border
              border-red-200
              rounded-xl
              shadow-sm
              p-8
              mb-5
              text-center
            "
          >

            <div
              className="
                mx-auto
                w-12
                h-12
                rounded-full
                bg-red-50
                text-red-500
                flex
                items-center
                justify-center
              "
            >
              <MdCancel size={24} />
            </div>

            <h3 className="mt-4 text-sm font-semibold text-slate-800">
              Unable to load users
            </h3>

            <p className="mt-1 text-sm text-slate-500">
              {error}
            </p>

            <button
              type="button"
              onClick={fecthAllUsers}
              className="
                mt-4
                inline-flex
                items-center
                gap-2
                px-4
                py-2
                rounded-lg
                bg-sky-600
                hover:bg-sky-700
                text-white
                text-sm
                font-medium
              "
            >
              <MdRefresh size={17} />
              Try Again
            </button>

          </div>

        )}

        {/* =================================================
            DATA TABLE
        ================================================= */}

        {!error && (

          <div
            className="
              bg-white
              border
              border-slate-200
              rounded-xl
              shadow-sm
              overflow-hidden
            "
          >

            {/* TABLE HEADER */}

            <div
              className="
                px-4
                sm:px-5
                py-3.5
                border-b
                border-slate-200
                flex
                flex-col
                sm:flex-row
                sm:items-center
                sm:justify-between
                gap-2
              "
            >

              <div>

                <h2 className="text-sm font-semibold text-slate-800">
                  Users
                </h2>

                <p className="text-xs text-slate-500 mt-0.5">
                  View and manage registered users
                </p>

              </div>

              <div className="text-xs text-slate-500">
                {filteredUsers.length} records
              </div>

            </div>

            {/* LOADING */}

            {loading ? (

              <div className="p-8">

                <div className="space-y-4 animate-pulse">

                  {[1, 2, 3, 4, 5].map((item) => (

                    <div
                      key={item}
                      className="
                        grid
                        grid-cols-5
                        gap-4
                        items-center
                      "
                    >

                      <div className="h-4 bg-slate-100 rounded" />
                      <div className="h-4 bg-slate-100 rounded" />
                      <div className="h-4 bg-slate-100 rounded" />
                      <div className="h-4 bg-slate-100 rounded" />
                      <div className="h-8 bg-slate-100 rounded" />

                    </div>

                  ))}

                </div>

                <p className="text-center text-xs text-slate-400 mt-5">
                  Loading users...
                </p>

              </div>

            ) : filteredUsers.length === 0 ? (

              /* EMPTY STATE */

              <div className="py-14 px-5 text-center">

                <div
                  className="
                    mx-auto
                    w-14
                    h-14
                    rounded-full
                    bg-slate-100
                    text-slate-400
                    flex
                    items-center
                    justify-center
                  "
                >
                  <MdPeopleOutline size={28} />
                </div>

                <h3 className="mt-4 text-sm font-semibold text-slate-800">
                  No users found
                </h3>

                <p className="mt-1 text-sm text-slate-500">
                  No users match your current search.
                </p>

                {searchTerm && (

                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    className="
                      mt-4
                      inline-flex
                      items-center
                      gap-2
                      px-4
                      py-2
                      rounded-lg
                      border
                      border-slate-200
                      bg-white
                      text-slate-600
                      text-sm
                      font-medium
                      hover:bg-slate-50
                    "
                  >
                    <MdRefresh size={17} />
                    Clear Search
                  </button>

                )}

              </div>

            ) : (

              /* TABLE */

              <div className="w-full overflow-x-auto">

                <DataTable
                  value={filteredUsers}
                  paginator
                  rows={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  className="w-full professional-users-table"
                  tableStyle={{
                    minWidth: "850px",
                  }}
                  rowHover
                  removableSort
                  paginatorTemplate="
                    FirstPageLink
                    PrevPageLink
                    PageLinks
                    NextPageLink
                    LastPageLink
                    RowsPerPageDropdown
                  "
                >

                  {/* ID */}

                  <Column
                    field="id"
                    header="ID"
                    sortable
                    style={{
                      width: "90px",
                    }}
                    body={(row) => (
                      <span className="text-xs font-semibold text-slate-500">
                        #{row.id}
                      </span>
                    )}
                  />

                  {/* USERNAME */}

                  <Column
                    field="username"
                    header="User"
                    sortable
                    body={usernameBodyTemplate}
                    style={{
                      width: "230px",
                    }}
                  />

                  {/* DEPARTMENT */}

                  <Column
                    field="department"
                    header="Department"
                    sortable
                    body={departmentBodyTemplate}
                    style={{
                      width: "180px",
                    }}
                  />

                  {/* Branch */}

                  <Column
                    field="branch"
                    header="Branch"
                    sortable
                    body={branchBodyTemplate}
                    style={{
                      width: "180px",
                    }}
                  />

                  {/* STATUS */}

                  <Column
                    field="is_active"
                    header="Status"
                    sortable
                    body={statusBodyTemplate}
                    style={{
                      width: "140px",
                    }}
                  />

                  {/* ROLE */}

                  <Column
                    field="role"
                    header="Role"
                    sortable
                    body={roleBodyTemplate}
                    style={{
                      width: "160px",
                    }}
                  />

                  {/* ACTION */}

                  <Column
                    header="Actions"
                    body={actionBodyTemplate}
                    style={{
                      width: "140px",
                    }}
                  />

                </DataTable>

              </div>

            )}

          </div>

        )}

      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <Toast ref={toast} />

      {/* =====================================================
          PRIME REACT TABLE STYLING
      ===================================================== */}

      <style>{`

        .professional-users-table .p-datatable-thead > tr > th {
          background: #f8fafc;
          color: #475569;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          padding: 0.85rem 0.9rem;
          border-color: #e2e8f0;
          white-space: nowrap;
        }

        .professional-users-table .p-datatable-tbody > tr > td {
          padding: 0.85rem 0.9rem;
          border-color: #f1f5f9;
          color: #475569;
          font-size: 0.875rem;
          vertical-align: middle;
        }

        .professional-users-table
          .p-datatable-tbody
          > tr:hover {
          background: #f8fafc;
        }

        .professional-users-table .p-paginator {
          background: #ffffff;
          border: 0;
          border-top: 1px solid #e2e8f0;
          padding: 0.65rem 0.85rem;
          justify-content: flex-end;
          gap: 0.15rem;
        }

        .professional-users-table
          .p-paginator
          .p-paginator-page,
        .professional-users-table
          .p-paginator
          .p-paginator-next,
        .professional-users-table
          .p-paginator
          .p-paginator-prev,
        .professional-users-table
          .p-paginator
          .p-paginator-first,
        .professional-users-table
          .p-paginator
          .p-paginator-last {

          min-width: 2rem;
          height: 2rem;
          border-radius: 0.5rem;
          font-size: 0.75rem;
        }

        .professional-users-table
          .p-paginator
          .p-highlight {

          background: #0284c7;
          color: white;
        }

        .professional-users-table .p-dropdown {
          height: 2rem;
          border-radius: 0.5rem;
          border-color: #e2e8f0;
          font-size: 0.75rem;
        }

        .professional-users-table
          .p-sortable-column-icon {

          width: 0.8rem;
          height: 0.8rem;
        }

        .professional-users-table
          .p-datatable-wrapper {

          scrollbar-width: thin;
        }

        @media (max-width: 640px) {

          .professional-users-table
            .p-datatable-thead
            > tr
            > th {

            padding: 0.7rem;
          }

          .professional-users-table
            .p-datatable-tbody
            > tr
            > td {

            padding: 0.7rem;
          }

          .professional-users-table
            .p-paginator {

            justify-content: center;
            flex-wrap: wrap;
          }
        }

      `}</style>

    </div>
  );
}